import { Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import type { TherapistResult } from "@/hooks/use-chat";
import { ChatMessage } from "./chat-message";
import { TherapistResultCard } from "./therapist-result-card";

interface Message {
	id: string;
	role: "user" | "bot";
	content: string;
	therapists?: TherapistResult[];
}

interface ChatWindowProps {
	messages: Message[];
	isLoading: boolean;
}

export function ChatWindow({ messages, isLoading }: ChatWindowProps) {
	const scrollRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	}, []);

	if (messages.length === 0) {
		return (
			<div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
				<div className="mb-3 text-4xl">💬</div>
				<h3 className="text-lg font-semibold">AI Therapist Search</h3>
				<p className="mt-1 max-w-md text-sm text-muted-foreground">
					Search for available therapists by typing naturally. Try:
					<br />
					<code className="text-xs rounded bg-muted px-1.5 py-0.5">
						Female therapist near Jakarta Selatan next Monday at 2pm
					</code>
				</p>
			</div>
		);
	}

	return (
		<div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
			{messages.map((msg) => (
				<div key={msg.id}>
					<ChatMessage role={msg.role} content={msg.content} />
					{msg.therapists && msg.therapists.length > 0 && (
						<div className="mb-4 ml-10 space-y-2">
							{msg.therapists.map((t) => (
								<TherapistResultCard key={t.id} therapist={t} />
							))}
						</div>
					)}
				</div>
			))}
			{isLoading && (
				<div className="flex items-center gap-2 mb-4 ml-2">
					<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
						AI
					</div>
					<div className="rounded-xl bg-muted px-4 py-2.5 rounded-tl-sm">
						<Loader2 className="size-4 animate-spin" />
					</div>
				</div>
			)}
		</div>
	);
}
